/**
 * OpVisualTrigger - one OPVisual action on a button: a cell (`L3-5`), a
 * column, the chain, clear a layer, STOP ALL, blackout, panic or reload.
 *
 * ## "Sent", never "on air"
 *
 * Nothing relays OPVisual's landing status to OPButtons yet, so this button
 * cannot know whether its cell is playing. After a successful send it shows a
 * SENT mark (`data-opv-sent`, a dashed edge) - deliberately not `data-on`,
 * which every other button uses to mean "this is live". A bar-quantised fire
 * is up to a bar away from landing, and lighting it on the tap would show the
 * operator a look the audience does not have yet. When the Phase 2 status
 * relay lands, `data-on` becomes the real on-air signal.
 *
 * ## Refused until it knows the machine
 *
 * See `sendOpVisual`: a send to the wrong bridge is accepted and silently
 * lost, so an unaimed Trigger is dead with "pick computer" on its face. The
 * spawner auto-picks whenever the answer is unambiguous, so the operator's
 * one-OPVisual rig never sees that state.
 *
 * ## "Not linked" is shown, never enforced
 *
 * On the OPBridge channel (DEC-215, the default) a press no OPVisual heard
 * comes back `app-not-linked`: nothing is marked sent and the face reads "not
 * linked". The button stays pressable, because the next press is the only
 * re-check (see `opvNotLinked`).
 */

import { createSignal, onCleanup, Show } from 'solid-js';
import { editOwnsGesture, selectedItemId, type LayoutColor, type OpVisualTriggerItem } from '../layout/store.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { pushToast } from '../ui/toast.ts';
import { fireOpVisualAction, opvFault, opvFaultLabel, opvNotLinked, reportOpv, type OpvAim } from '../opvisual/fire.ts';
import { opvSession } from '../opvisual/session.ts';
import { actionFace, verbTakesQuant } from '../opvisual/wire.ts';
import { cellKey, opvListing, watchOpvListing } from '../opvisual/listing.ts';
import { PadDecor, padAttrs } from './PadDecor.tsx';

interface Props {
  item: OpVisualTriggerItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

/** Verbs worth a confirming toast. A cell or column is fired dozens of times
 *  a set and its face already flashes; these are the rare, heavy ones whose
 *  success the operator wants spelled out. */
const TOAST_ON_OK = new Set(['stopAll', 'blackout', 'operatorScreen', 'panic', 'reloadLibrary', 'palette', 'streamSafe']);

export function OpVisualTrigger(props: Props) {
  const guard = createDiscreteGuard(() => !!props.item.guard);
  const [pressed, setPressed] = createSignal(false);
  const [firing, setFiring] = createSignal(false);

  const fault = () => opvFault(props.item.bridgeIds);
  const live = () => fault() === null;
  const aim = (): OpvAim => ({ bridgeIds: props.item.bridgeIds, via: props.item.sendVia });
  const notLinked = () => opvNotLinked(aim());

  // A cell trigger shows OPVisual's picture of that clip under its text (operator, 2026-10-13: "is it possible to
  // get the opvisual cell thumbnail on the button (under the title text layer)"). Same listing as the Grid
  // (`opvisual/listing.ts`, fetched through OPBridge from the machine the item sends to, pictures cached per thumb).
  const isCell = () => props.item.action.verb === 'cell';
  const release = watchOpvListing(props.item.bridgeIds?.[0]);
  onCleanup(release);
  const cellImg = (): string | null => {
    const a = props.item.action;
    if (a.verb !== 'cell') return null;
    return opvListing(props.item.bridgeIds?.[0])?.cells[cellKey(a.col, a.layer)]?.img ?? null;
  };

  const sent = (): boolean => {
    const a = props.item.action;
    const s = opvSession();
    if (a.verb === 'cell') return s.sentCell[a.layer - 1] === a.col;
    if (a.verb === 'column') return s.sentColumn === a.col;
    return false;
  };

  async function fire(): Promise<void> {
    setFiring(true);
    try {
      const r = await fireOpVisualAction(props.item.action, props.item.quant, aim());
      if (r.kind !== 'ok') {
        reportOpv(r);
      } else if (TOAST_ON_OK.has(props.item.action.verb)) {
        pushToast('ok', `OPVisual ${actionFace(props.item.action).toLowerCase()} sent`, 1500);
      }
    } finally {
      setFiring(false);
    }
  }

  function onPointerDown(e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    if (selectedItemId() === props.item.id) return;
    if (!live()) return;
    e.stopPropagation();
    if (!guard.gate('fire')) return;
    setPressed(true);
    setTimeout(() => setPressed(false), 120);
    void fire();
  }

  const faceText = () => props.item.name ?? actionFace(props.item.action);
  const subText = () => {
    const f = fault();
    if (f) return opvFaultLabel(f);
    if (firing()) return 'sending...';
    if (guard.armedKey() === 'fire') return 'tap again';
    if (notLinked()) return 'not linked';
    if (verbTakesQuant(props.item.action.verb)) {
      return `${props.item.quant === 'now' ? 'now' : 'on the bar'}${sent() ? ' - sent' : ''}`;
    }
    return 'OPVisual';
  };

  return (
    <button
      class="midi-button opv-trigger"
      {...padAttrs(props.item)}
      type="button"
      data-on="false"
      data-opv-sent={sent() ? 'true' : 'false'}
      data-sent={sent() ? 'true' : undefined}
      data-link={notLinked() ? 'down' : 'ok'}
      data-pressed={pressed() ? 'true' : 'false'}
      data-armed={guard.armedKey() === 'fire' ? 'true' : 'false'}
      data-color={COLOR_TOKEN[props.item.color]}
      data-shape="rect"
      data-family="rect"
      data-img={isCell() && cellImg() ? 'true' : 'false'}
      onPointerDown={onPointerDown}
      disabled={!live()}
    >
      <PadDecor item={props.item} />
      <Show when={isCell() && cellImg()}>
        {(src) => <img class="opv-trigger-img" src={src()} alt="" draggable={false} />}
      </Show>
      {/* SENT (DEC-321 rule 1): OPButtons knows only that it sent; OPVisual's own report lights the grid, not this. */}
      <Show when={sent()}><span class="sent-tag" aria-label="sent">SENT</span></Show>
      {!props.item.hideName && (
        <span class="midi-button-name">
          <span class="face-line">{faceText()}</span>
        </span>
      )}
      {!props.item.hideLabel && <span class="midi-button-label">{subText()}</span>}
    </button>
  );
}
